import { CURSOR_PLACEHOLDER } from '@/components/BlinkingCursor';

/**
 * Every Signals answer that reports figures closes with a fixed two-line
 * signature the model is instructed to write:
 *
 *   📘 How it was computed: <one line, always English>
 *   Data updated through <date>
 *
 * Rendered as markdown they become two ordinary grey paragraphs — an English
 * sentence stranded at the bottom of an Arabic answer, and a date that the
 * surrounding RTL run pushes to the wrong end of its line. They are not
 * sentences in the reply; they are properties of it, and the redesign gives
 * each its own block.
 *
 * Splitting the source string rather than the rendered nodes, because the two
 * lines usually live inside the same markdown paragraph as whatever precedes
 * them, and a paragraph cannot be taken apart after react-markdown has built it.
 */

// The colon lands inside or outside the bold depending on how the model wrote
// it: "📘 How it was computed: x", "**How it was computed:** x", "**How it was
// computed**: x".
const METHOD_RE =
  /^\s*(?:📘\s*)?\*{0,2}\s*How it was computed\s*:?\s*\*{0,2}\s*:?\s*/i;
const FRESHNESS_RE = /^\s*(?:\*\*)?Data updated through\b/i;

export interface AnswerSignature {
  body: string;
  /** The method line with its label stripped — the label becomes the block's own. */
  method?: string;
  /** The freshness line, kept whole. */
  freshness?: string;
}

export const splitAnswerSignature = (source: string): AnswerSignature => {
  if (!source) return { body: source };
  // Cheap reject: most markdown the chat renders is not an answer signature.
  // Unanchored on purpose — METHOD_RE is anchored to the start of a line, and
  // testing it against the whole source would only ever match an answer whose
  // very first line is the method line.
  if (
    !/How it was computed/i.test(source) &&
    !/Data updated through/i.test(source)
  ) {
    return { body: source };
  }

  const lines = source.split('\n');
  let method: string | undefined;
  let freshness: string | undefined;
  let end = lines.length;

  // Walk backwards from the end of the answer: the signature is always last.
  // Stop at the first line that is neither blank nor part of the signature, so
  // a sentence that merely mentions the phrase mid-answer is left alone.
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i];
    // A line still carrying the streaming cursor is half-written. Leave it in
    // the body until it finishes rather than rendering a partial block.
    if (line.includes(CURSOR_PLACEHOLDER)) break;

    if (!line.trim()) {
      end = i;
      continue;
    }
    if (freshness === undefined && FRESHNESS_RE.test(line)) {
      freshness = line.trim().replace(/^\*\*|\*\*$/g, '');
      end = i;
      continue;
    }
    if (method === undefined && METHOD_RE.test(line)) {
      method = line.trim().replace(METHOD_RE, '').replace(/\*\*$/, '').trim();
      end = i;
      continue;
    }
    break;
  }

  if (method === undefined && freshness === undefined) return { body: source };

  return {
    body: lines.slice(0, end).join('\n').replace(/\s+$/, ''),
    method: method || undefined,
    freshness
  };
};
